import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, Loader, Settings2, AlertCircle } from 'lucide-react';
import { useOnClickOutside } from '../../hooks/useOnClickOutside';
import { kanbanApiService } from '../../services/kanbanApi';
import type { KanbanBoardSetting } from '../../types/kanban';
import { cn } from '../../utils/cn';

interface KanbanBoardSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  boardId: string;
  settings: KanbanBoardSetting[];
  onUpdated: () => void;
}

const COMMON_SETTING_KEYS = ['wip_aging_warning_hours', 'default_card_type'] as const;

export const KanbanBoardSettingsModal = ({
  isOpen,
  onClose,
  boardId,
  settings,
  onUpdated,
}: KanbanBoardSettingsModalProps) => {
  const modalRef = useRef<HTMLDivElement>(null);
  useOnClickOutside(modalRef, onClose);

  const [valueByKey, setValueByKey] = useState<Record<string, string>>({});
  const [newKey, setNewKey] = useState('');
  const [newValue, setNewValue] = useState('');
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load settings from API and sync with prop
  const refreshSettings = async () => {
    try {
      const fresh = await kanbanApiService.listBoardSettings(boardId);
      const map: Record<string, string> = {};
      for (const s of fresh) {
        map[s.setting_key] = s.setting_value;
      }
      setValueByKey(map);
    } catch (e) { console.error(e); }
  };

  useEffect(() => {
    if (!isOpen) return;
    setError(null);
    const map: Record<string, string> = {};
    const rows = Array.isArray(settings) ? settings : [];
    for (const s of rows) {
      map[s.setting_key] = s.setting_value;
    }
    setValueByKey(map);
    setNewKey('');
    setNewValue('');
    setSavingKey(null);
    setIsAdding(false);
    // Also refresh from API for latest data
    void refreshSettings();
  }, [isOpen, settings]);

  const handleRowSave = async (key: string) => {
    setSavingKey(key);
    setError(null);
    try {
      await kanbanApiService.setBoardSetting(boardId, key, {
        setting_value: valueByKey[key] ?? '',
      });
      // Verify the setting was saved correctly
      await kanbanApiService.getBoardSetting(boardId, key);
      onUpdated();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save setting.');
    } finally {
      setSavingKey(null);
    }
  };

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    const k = newKey.trim();
    if (!k) {
      setError('Setting key is required.');
      return;
    }
    setIsAdding(true);
    setError(null);
    try {
      await kanbanApiService.setBoardSetting(boardId, k, {
        setting_value: newValue,
      });
      setNewKey('');
      setNewValue('');
      onUpdated();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add setting.');
    } finally {
      setIsAdding(false);
    }
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className="modal-overlay z-[9999] overflow-y-auto">
      <div
        ref={modalRef}
        className="modal-panel max-w-2xl"
      >
        <button
          type="button"
          onClick={onClose}
          className="modal-close z-10"
          aria-label="Close"
          disabled={!!savingKey || isAdding}
        >
          <X className="w-5 h-5" />
        </button>

        <div className="modal-header">
          <div className="min-w-0">
            <h2 className="modal-title flex items-center gap-2">
              <Settings2 className="w-5 h-5 text-accent-400 shrink-0" />
              Board settings
            </h2>
            <p className="modal-subtitle">
              Common keys: <span className="font-mono text-xs">{COMMON_SETTING_KEYS.join(', ')}</span>.
            </p>
          </div>
        </div>

        {error && (
          <div className="alert alert-danger mb-3" role="alert">
            <AlertCircle />
            <span>{error}</span>
          </div>
        )}

        <div className="subcard overflow-x-auto mb-4">
          <table className="table">
            <thead>
              <tr>
                <th className="th !px-3">Key</th>
                <th className="th !px-3">Value</th>
                <th className="th !px-3 w-28" />
              </tr>
            </thead>
            <tbody>
              {settings.length === 0 ? (
                <tr>
                  <td colSpan={3} className="td !px-3 text-sm text-glass-500 text-center">
                    No settings yet. Add one below.
                  </td>
                </tr>
              ) : (
                settings.map((row) => (
                  <tr key={row.setting_key}>
                    <td className="td !py-2 !px-3 align-middle font-mono text-xs text-glass-200">{row.setting_key}</td>
                    <td className="td !py-2 !px-3 align-middle">
                      <input
                        value={valueByKey[row.setting_key] ?? ''}
                        onChange={(e) =>
                          setValueByKey((prev) => ({ ...prev, [row.setting_key]: e.target.value }))
                        }
                        className="glass-input w-full"
                        disabled={savingKey === row.setting_key}
                      />
                    </td>
                    <td className="td !py-2 !px-3 align-middle">
                      <button
                        type="button"
                        className="glass-button-sm w-full"
                        onClick={() => handleRowSave(row.setting_key)}
                        disabled={savingKey === row.setting_key}
                      >
                        {savingKey === row.setting_key ? (
                          <Loader className="w-3.5 h-3.5 animate-spin" />
                        ) : null}
                        Save
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <form onSubmit={handleAdd} className="space-y-3 border-t border-glass-200/10 pt-4">
          <p className="text-sm font-semibold text-white">Add setting</p>
          <div className="grid gap-2 sm:grid-cols-2">
            <div>
              <label htmlFor="new-setting-key" className="text-xs font-medium text-glass-400 mb-1.5 block">
                Key
              </label>
              <input
                id="new-setting-key"
                value={newKey}
                onChange={(e) => setNewKey(e.target.value)}
                className="glass-input w-full font-mono"
                placeholder="e.g. wip_aging_warning_hours"
                disabled={isAdding}
              />
            </div>
            <div>
              <label htmlFor="new-setting-value" className="text-xs font-medium text-glass-400 mb-1.5 block">
                Value
              </label>
              <input
                id="new-setting-value"
                value={newValue}
                onChange={(e) => setNewValue(e.target.value)}
                className="glass-input w-full"
                placeholder="e.g. 48"
                disabled={isAdding}
              />
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="glass-button-secondary" onClick={onClose} disabled={isAdding || !!savingKey}>
              Close
            </button>
            <button
              type="submit"
              className={cn('btn-primary', isAdding && 'opacity-70')}
              disabled={isAdding}
            >
              {isAdding ? <Loader className="w-4 h-4 animate-spin" /> : null}
              Set value
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};
